import {
  Button,
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  SharedSelection,
  Skeleton
} from '@heroui/react';
import { NavArrowDown } from 'iconoir-react';
import { isEqual } from 'lodash';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';

import { useContentHeaderContext } from './ContentHeaderContext';
import type { ActiveOptionsType } from './types';

import i18n from '~/common/helpers/i18n';

type BaseFilterSelectProps = {
  label?: string;
  options: { value: string; label: string }[];
  selectionMode?: 'single' | 'multiple';
  displayCount?: boolean;
  selectionLimit?: number;
  className?: string;
  ariaLabel?: string;
  'data-testid'?: string;
};

type ContextModeProps = BaseFilterSelectProps & {
  standalone?: false;
  filterKey: string;
  onSelect?: (
    selectedKey: string[],
    filterKey: string,
    activeOptions: ActiveOptionsType
  ) => void;
  selectedKey?: never;
  onSelectionChange?: never;
};

type StandaloneModeProps<T extends string = string> = BaseFilterSelectProps & {
  standalone: true;
  selectedKey: T;
  onSelectionChange: (key: T) => void;
  filterKey?: never;
  onSelect?: never;
};

export type ContentHeaderFilterSelectProps<T extends string = string> =
  | ContextModeProps
  | StandaloneModeProps<T>;

export function ContentHeaderFilterSelect<T extends string = string>(
  props: ContentHeaderFilterSelectProps<T>
) {
  const {
    label,
    options,
    selectionMode = 'single',
    displayCount = true,
    selectionLimit,
    className,
    ariaLabel,
    'data-testid': dataTestId
  } = props;

  const isStandalone = 'standalone' in props && props.standalone === true;
  const filterKey = !isStandalone ? (props as ContextModeProps).filterKey : '';
  const onSelect = !isStandalone
    ? (props as ContextModeProps).onSelect
    : undefined;
  const standaloneSelectedKey = isStandalone
    ? (props as StandaloneModeProps<T>).selectedKey
    : ('' as T);
  const standaloneOnChange = isStandalone
    ? (props as StandaloneModeProps<T>).onSelectionChange
    : undefined;

  const { handleSelectFilter, activeOptions, isLoaded } =
    useContentHeaderContext('ContentHeaderFilterSelect');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getStandaloneValues = (key: string): string[] => (key ? [key] : []);

  const getInitialValues = useCallback((): string[] => {
    if (isStandalone) {
      return getStandaloneValues(standaloneSelectedKey);
    }
    return activeOptions.filters?.[filterKey] || [];
  }, [isStandalone, standaloneSelectedKey, activeOptions.filters, filterKey]);

  const valuesBeforeOpen = useRef<string[]>(getInitialValues());
  const [localSelectedValues, setLocalSelectedValues] =
    useState<string[]>(getInitialValues());

  const contextSelectedValues = useMemo(() => {
    if (isStandalone) {
      return getStandaloneValues(standaloneSelectedKey);
    }
    return activeOptions.filters?.[filterKey] || [];
  }, [isStandalone, standaloneSelectedKey, activeOptions.filters, filterKey]);

  const limitReached =
    selectionLimit && localSelectedValues.length >= selectionLimit;

  const isNonDefaultSelected =
    selectionMode === 'multiple'
      ? localSelectedValues.length > 0
      : localSelectedValues.length > 0 &&
        localSelectedValues[0] !== options[0]?.value;

  useEffect(() => {
    if (isStandalone) {
      setLocalSelectedValues(
        standaloneSelectedKey ? [standaloneSelectedKey] : []
      );
    } else {
      setLocalSelectedValues(activeOptions.filters?.[filterKey] || []);
    }
  }, [isStandalone, standaloneSelectedKey, activeOptions.filters, filterKey]);

  const handleSelectionChange = useCallback(
    (keys: SharedSelection) => {
      if (selectionMode === 'single') {
        const selectedKey = keys.currentKey;
        if (selectedKey) {
          if (isStandalone) {
            standaloneOnChange?.(selectedKey as T);
          } else {
            handleSelectFilter?.(filterKey, [selectedKey]);
            onSelect?.([selectedKey], filterKey, {
              ...activeOptions,
              filters: { ...activeOptions.filters, [filterKey]: [selectedKey] }
            });
          }
          setLocalSelectedValues([selectedKey]);
        }
      } else {
        const selectedKeys = Array.from(keys).map(key =>
          (key as string).toString()
        );
        setLocalSelectedValues(selectedKeys);
      }
    },
    [
      selectionMode,
      isStandalone,
      standaloneOnChange,
      handleSelectFilter,
      filterKey,
      onSelect,
      activeOptions
    ]
  );

  const handleClose = useCallback(() => {
    if (isEqual(localSelectedValues, valuesBeforeOpen.current)) return;

    if (selectionMode === 'multiple' && !isStandalone) {
      handleSelectFilter?.(filterKey, localSelectedValues);
      onSelect?.(localSelectedValues, filterKey, {
        ...activeOptions,
        filters: { ...activeOptions.filters, [filterKey]: localSelectedValues }
      });
    }
  }, [
    localSelectedValues,
    selectionMode,
    isStandalone,
    handleSelectFilter,
    filterKey,
    onSelect,
    activeOptions
  ]);

  const clearSelection = useCallback(() => {
    setLocalSelectedValues([]);
  }, []);

  const applySelection = useCallback(() => {
    if (!isStandalone) {
      handleSelectFilter?.(filterKey, localSelectedValues);
      if (selectionMode === 'multiple') {
        onSelect?.(localSelectedValues, filterKey, {
          ...activeOptions,
          filters: {
            ...activeOptions.filters,
            [filterKey]: localSelectedValues
          }
        });
      }
    }
    setIsDropdownOpen(false);
  }, [
    isStandalone,
    handleSelectFilter,
    filterKey,
    localSelectedValues,
    selectionMode,
    onSelect,
    activeOptions
  ]);

  const selectedLabel =
    options.find(opt => opt.value === localSelectedValues[0])?.label ||
    label ||
    options[0]?.label;

  if (!isLoaded && !isStandalone) {
    return <Skeleton className='h-[30px] w-20 rounded-md' />;
  }

  if (!options.length) {
    return null;
  }

  return (
    <Dropdown
      onClose={handleClose}
      isOpen={isDropdownOpen}
      onOpenChange={v => {
        setIsDropdownOpen(v);
        if (v) {
          setLocalSelectedValues(contextSelectedValues);
          valuesBeforeOpen.current = [...contextSelectedValues];
        }
      }}
      classNames={{ content: 'p-0 pl-1.5' }}
      className='rounded-md bg-white shadow-xl'
    >
      <DropdownTrigger data-testid={dataTestId}>
        <Button
          variant='bordered'
          className={cn(
            'transition-colors border border-outline text-title-sm py-1 px-2 h-auto min-w-0',
            isNonDefaultSelected &&
              'border-[#00000052] bg-surface-container-low !text-label-lg',
            className
          )}
          endContent={<NavArrowDown className='h-4 w-4' />}
        >
          <span className='text-sm'>
            {selectionMode === 'single' ? selectedLabel : label}
          </span>
          {displayCount &&
            selectionMode === 'multiple' &&
            localSelectedValues.length > 0 && (
              <span className='flex h-5 min-w-5 items-center justify-center rounded-sm bg-surface-container-low px-1 text-xs leading-tight tracking-wide text-on-surface-variant'>
                {`${localSelectedValues.length}`}
              </span>
            )}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={ariaLabel}
        selectionMode={selectionMode}
        classNames={{ base: 'p-0' }}
        onSelectionChange={handleSelectionChange}
        closeOnSelect={selectionMode === 'single'}
        selectedKeys={
          selectionMode === 'single'
            ? [localSelectedValues[0]]
            : localSelectedValues
        }
        disabledKeys={
          limitReached && selectionMode === 'multiple'
            ? options
                .filter(option => !localSelectedValues.includes(option.value))
                .map(option => option.value)
            : []
        }
      >
        <DropdownSection className='mb-0 max-h-[212px] overflow-y-auto pb-2 pr-1.5 pt-2 text-sm'>
          <>
            {selectionMode === 'multiple' && selectionLimit && (
              <DropdownItem
                key='header'
                className='mb-2 flex cursor-default items-center justify-between !bg-transparent leading-none tracking-tight !text-on-surface-variant'
                isReadOnly
                hideSelectedIcon
              >
                <span>
                  {i18n.t('scenes.pipelines.labels.filter_limitation', {
                    max: selectionLimit
                  })}
                </span>
              </DropdownItem>
            )}
            {options.map(option => (
              <DropdownItem
                key={option.value}
                value={option.value}
                textValue={option.label}
                className='!text-label-lg text-on-surface-variant aria-checked:text-on-surface'
                data-testid={
                  dataTestId
                    ? `${dataTestId}-menu-item-${option.value.toLowerCase().replace(/\s+/g, '-')}`
                    : undefined
                }
              >
                <span>{option.label}</span>
              </DropdownItem>
            ))}
          </>
        </DropdownSection>
        {selectionMode === 'multiple' ? (
          <DropdownSection className='mb-0 border-t-1 border-solid border-outline'>
            <DropdownItem
              key='actions'
              isReadOnly
              hideSelectedIcon
              className='mb-3 mt-3 cursor-default !bg-transparent p-0 hover:!bg-transparent'
            >
              <div className='flex items-center justify-between pr-4'>
                <Button
                  variant='light'
                  onPress={clearSelection}
                  className='min-w-16 px-2.5 py-1.5 font-semibold leading-tight tracking-tight'
                >
                  {i18n.t('scenes.pipelines.labels.filter_clear')}
                </Button>
                <Button
                  variant='bordered'
                  color='primary'
                  onPress={applySelection}
                  className='min-w-16 border-1 px-2.5 py-1.5 font-semibold leading-tight tracking-tight'
                >
                  {i18n.t('scenes.pipelines.labels.filter_apply')}
                </Button>
              </div>
            </DropdownItem>
          </DropdownSection>
        ) : null}
      </DropdownMenu>
    </Dropdown>
  );
}
